import { AppSidebar } from '@mxa-argos/components/custom/app-sidebar';
import { Header } from '@mxa-argos/components/custom/header';
import { WorktreeRibbon } from '@mxa-argos/components/custom/worktree-ribbon';
import { SidebarInset, SidebarProvider } from '@mxa-argos/components/shadcn/sidebar';
import { cn } from '@mxa-argos/lib/utils';
import {
  createContext,
  type PropsWithChildren,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

const SIDEBAR_STORAGE_KEY = 'sidebar_open';

// eslint-disable-next-line react-refresh/only-export-components
export const FullBleedContainerContext = createContext<RefObject<HTMLDivElement | null> | null>(
  null,
);

function readSidebarState(): boolean {
  try {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
}

interface AdminLayoutProps extends PropsWithChildren {
  variant?: 'table' | 'content';
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children, variant = 'table' }) => {
  const [open, setOpenState] = useState(readSidebarState);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const setOpen = useCallback((value: boolean) => {
    setOpenState(value);
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(value));
    } catch {
      // localStorage unavailable
    }
  }, []);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === SIDEBAR_STORAGE_KEY && event.newValue !== null) {
        setOpenState(event.newValue === 'true');
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  return (
    <SidebarProvider open={open} onOpenChange={setOpen}>
      <AppSidebar />
      <SidebarInset className="flex flex-col h-dvh overflow-hidden">
        <Header />
        <div
          ref={scrollContainerRef}
          className={cn(
            'flex-1 flex flex-col min-h-0 p-3 sm:p-6 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:pb-[calc(1.5rem+env(safe-area-inset-bottom))]',
            variant === 'content' && 'overflow-y-auto',
          )}
        >
          <div
            className={cn(
              'w-full 2xl:w-[90%] 2xl:mx-auto flex flex-col gap-3',
              variant === 'table' && 'h-full',
              variant === 'content' && 'flex-1',
            )}
          >
            <FullBleedContainerContext.Provider value={scrollContainerRef}>
              {children}
            </FullBleedContainerContext.Provider>
          </div>
        </div>
      </SidebarInset>
      <WorktreeRibbon />
    </SidebarProvider>
  );
};
